import { Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ContactOwnerModal from "../../../components/modals/ContactOwnerModal";
import ContactUs from "../../../components/public/userCommon/ContactUs";
import api from "../../../configs/api";
import useContactModal from "../../../hooks/useContactModal";
import { formatPrettyAddress, formatPrice } from "../../../utils/helpers";
import { IoLocationOutline } from "react-icons/io5";
import { MapEmbed } from "../../../components/admin/common/details-components";

const LandProperties = ({ property, openModal }) => {
  const [mainImage, setMainImage] = useState(
    property?.images?.find((img) => img.isThumbnail)?.url ||
      property?.images?.[0]?.url ||
      ""
  );

  const landImages = property?.images || [];

  return (
    <div className="bg-white px-3 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8 lg:py-10 border rounded-lg shadow-sm">
      <div className="mx-auto rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Section - Images */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
            {/* Thumbnails Column */}
            <div
              className="flex sm:flex-col flex-wrap sm:flex-nowrap gap-2 sm:gap-3 w-full sm:w-24 order-2 sm:order-1 justify-center"
              style={{
                maxHeight: "416px",
              }}
            >
              {landImages.slice(0, 4).map((thumb, idx) => (
                <div
                  key={idx}
                  onClick={() => setMainImage(thumb.url)}
                  className={`w-[70px] h-[70px] sm:w-24 sm:h-24 rounded-lg overflow-hidden cursor-pointer border-2 `}
                >
                  <img
                    src={thumb.url}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              ))}
            </div>

            {/* Main Image */}
            <div className="flex-1 rounded-lg overflow-hidden order-1 sm:order-2 w-full">
              <div className="relative w-full h-[250px] sm:h-[350px] md:h-[420px]">
                <img
                  src={mainImage}
                  alt={property?.title || "Land Property"}
                  className="w-full h-full object-cover transition-all duration-500"
                />
              </div>
            </div>
          </div>

          {/* Right Section - Details */}
          <div className="p-3 sm:p-4 md:p-6 lg:p-8 flex flex-col justify-between bg-white">
            <div>
              {/* Location */}
              {property?.location && (
                <div className="text-sm flex gap-2 items-center sm:text-base font-normal text-gray-600 mb-1">
                  <IoLocationOutline />
                  {formatPrettyAddress(property?.location)}
                </div>
              )}

              {/* Title */}
              <h1 className="text-base sm:text-lg md:text-2xl lg:text-3xl font-semibold mb-2 text-gray-900 leading-snug mt-2 sm:mt-4">
                {property?.title || "Property Title Not Available"}
              </h1>

              {/* Listing ID */}
              <div className="text-xs sm:text-sm md:text-base font-normal text-gray-600 mt-3 sm:mt-4">
                Listing ID: {property?._id || "N/A"}
              </div>

              {/* Price and Area */}
              <div className="mt-4">
                <div className="text-base sm:text-lg md:text-2xl font-semibold text-gray-900 mb-2">
                  {property?.property?.expectedPrice
                    ? formatPrice(property.property.expectedPrice)
                    : "Price on Request"}
                </div>
                <div className="text-sm sm:text-base font-medium text-gray-600">
                  Plot Area:{" "}
                  <span className="font-normal text-gray-600">
                    {property?.property?.carpetArea?.value
                      ? `${property.property.carpetArea.value} ${property.property.carpetArea.unit}`
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button className="w-full sm:flex-1 px-5 py-2 sm:py-3 bg-white border-2 border-red-600 text-red-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                View Number
              </button>

              <button
                onClick={() => openModal()}
                className="w-full sm:flex-1 px-5 py-2 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                Contact Owner
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutLands = ({ property, openModal }) => {
  const { property: propDetails, location } = property;
  const aboutData = [
    {
      label: "Plot Area",
      value: propDetails?.carpetArea
        ? `${propDetails.carpetArea.value} ${propDetails.carpetArea.unit}`
        : "N/A",
    },

    {
      label: "Width of road facing",
      value: (() => {
        const w = propDetails?.widthOfRoadFacing;

        if (w && typeof w === "object" && "value" in w && "unit" in w) {
          return `${w.value} ${w.unit}`;
        }

        if (typeof w === "number") {
          return `${w} sqft`;
        }

        return "N/A";
      })(),
    },
    {
      label: "Floors allowed for construction",
      value: propDetails?.floorsAllowedForConstruction || "N/A",
    },
    {
      label: "Pre Leased Property",
      value:
        propDetails?.isPreleasedProperty === true
          ? "Yes"
          : propDetails?.isPreleasedProperty === false
          ? "No"
          : "N/A",
    },

    {
      label: "No. of Open Sides",
      value: propDetails?.noOfOpenSides || "N/A",
    },
    {
      label: "Address",
      value: location?.address?.formatted || "N/A",
    },
  ];

  if (!property) {
    return (
      <div className="w-full bg-white p-6 rounded-xl border-2 text-center text-gray-600">
        Loading property details...
      </div>
    );
  }
  return (
    <div className="w-full">
      <div className="bg-white p-4 sm:p-4 md:p-6 lg:p-8 xl:p-10 rounded-xl border-2">
        {/* Heading */}
        <h1 className="text-lg sm:text-xl md:text-3xl font-semibold mb-1 sm:mb-2 md:mb-3 lg:mb-4 text-gray-900">
          More Details
        </h1>

        {/* Property Info List */}
        <div className="divide-y divide-gray-200 space-y-2 sm:space-y-3 lg:space-y-5">
          {aboutData.map((item, i) => (
            <div
              key={i}
              className="flex flex-row justify-between sm:items-center pt-4"
            >
              <span className="text-sm md:text-md lg:text-lg font-normal text-gray-600">
                {item.label} :
              </span>
              <span className="text-sm md:text-md lg:text-lg font-medium text-gray-900 break-words whitespace-normal max-w-[70%] sm:max-w-[60%] text-right sm:text-left">
                {item.value}
              </span>
            </div>
          ))}
        </div>

        {/* Contact Button */}
        {/* <div className="mt-10 flex">
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-medium text-sm sm:text-base md:text-2xl px-8 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <FaPhoneAlt className="text-base sm:text-lg" />
            Contact Owner
          </button>
        </div> */}
      </div>
    </div>
  );
};

const DetailsLandProperty = () => {
  const { id } = useParams();
  const { isOpen, openModal, closeModal } = useContactModal();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/properties/${id}`);
        // console.log("land by id===>", res.data);

        const data = res.data?.data;

        //  Ensure only "land" type properties are loaded
        if (data?.type === "land") {
          setProperty(data);
        } else {
          setError("This property is not of type 'land'.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load property.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProperty();
  }, [id]);

  //  Loading and error states
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <p className="text-lg font-semibold text-gray-600">
          Loading property details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <p className="text-lg font-semibold text-red-600">{error}</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <p className="text-lg font-semibold text-gray-600">
          No property details available.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      {/* Top section - Property images/details */}
      <LandProperties property={property} openModal={openModal} />

      {/* Main content area */}
      <div className="grid grid-cols-1 xl:grid-cols-4 px-4 sm:px-6 md:px-8 lg:px-10 gap-1 sm:gap-2 md:gap-4 xl:gap-10">
        {/* Left Section - About + Map */}
        <div className="lg:col-span-3 w-full">
          <div className="mt-8 sm:mt-10">
            <AboutLands property={property} openModal={openModal} />
          </div>
          <div className="py-6 sm:py-8 md:py-10">
            <MapEmbed
              className="h-[20rem] lg:h-[30rem]"
              coordinates={property?.location?.coordinates}
              address={formatPrettyAddress(property?.location)}
            />
          </div>
        </div>

        {/* Right Section - ContactUs */}
        <div className="lg:col-span-1 xl:mt-10 mb-4 sm:mb-6 md:mb-8 lg:mb-10">
          <ContactUs />
        </div>
      </div>
      <ContactOwnerModal
        isOpen={isOpen}
        onClose={closeModal}
        propertyData={property}
      />
    </div>
  );
};

export default DetailsLandProperty;
